<template>
  <div class="home-page-wrapper">
    <EventBackground v-if="isDiwaliActive" />
    <h1 class="v-sr-only">Equirize - Fixed Income Investments</h1>
    <HomeBanner class="banner-section" />
    <HomePlans :style="{ backgroundColor: isDiwaliActive ? 'transparent' : colors.primaryBg }" />
    <HomeLiveOpportunities heading="Live Opportunities" :style="{ backgroundColor: isDiwaliActive ? 'transparent' : colors.primaryBg }" />
    <HomeExploreSDG :style="{ backgroundColor: isDiwaliActive ? 'transparent' : colors.primaryBg }" />
    <HomeTrustedBy :style="{ backgroundColor: isDiwaliActive ? 'transparent' : colors.primaryBg }" />
    <HomeHowItWorks :steps="stepsData" :style="{ backgroundColor: isDiwaliActive ? 'transparent' : colors.primaryBg }" />
    <HomeEventBanner v-if="isDiwaliActive" :style="{ backgroundColor: isDiwaliActive ? 'transparent' : colors.primaryBg }" />
    <HomeReferBanner v-else :style="{ backgroundColor: colors.primaryBg }" />
    <testimonials :style="{ backgroundColor: isDiwaliActive ? 'transparent' : colors.secondaryBg }" />
    <news-swiper />
    <faq :style="{ backgroundColor: isDiwaliActive ? 'transparent' : colors.secondaryBg }" />
    <LazyBookACall :style="{ backgroundColor: isDiwaliActive ? 'transparent' : colors.secondaryBg }" />
  </div>
</template>

<script setup lang="ts">
import { ref } from "vue";
import { useTheme } from "~/composables/useTheme";
import { usePageView } from "~/composables/usePageView";
import { useRuntimeConfig } from "#app";
import EventBackground from "~/components/common/EventBackground.vue";
import {
  ANDROID_PACKAGE_NAME,
  APP_NAME,
  APP_STORE_URL,
  PLAY_STORE_URL,
} from "~/config/app.config";

const { colors } = useTheme();
const config = useRuntimeConfig();
const isDiwaliActive = ref(config.public.VITE_ENABLE_DIWALI_THEME === "true");

usePageView({ page_type: 'home' });

const schemaJson = JSON.stringify({
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": ["Organization", "FinancialService"],
      "@id": "https://www.equirize.com/#organization",
      "name": "EquiRize",
      "url": "https://www.equirize.com/",
      "logo": {
        "@type": "ImageObject",
        "url": "https://www.equirize.com/logo.png",
        "width": "512",
        "height": "512"
      },
      "image": "https://www.equirize.com/logo.png",
      "telephone": "+91-8712498971",
      "priceRange": "₹₹",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "301 Chawla Complex Commercial Premises, Sector 15, CBD Belapur",
        "addressLocality": "Navi Mumbai",
        "addressRegion": "Maharashtra",
        "postalCode": "400614",
        "addressCountry": "IN"
      },
      "sameAs": [
        "https://www.linkedin.com/company/equirize",
        "https://twitter.com/equirize",
        APP_STORE_URL,
        PLAY_STORE_URL
      ],
      "description": "Equirize is India's regulated debt investment platform offering bonds and fixed deposits. Compare FD rates, invest in corporate bonds, and earn stable returns.",
      "knowsAbout": [
        "Bonds",
        "Fixed Deposits",
        "Fixed Income Investments"
      ],
      "areaServed": "IN",
      "feesAndCommissionsSpecification": "Transparent pricing on listed bond transactions via existing Demat accounts.",
      "contactPoint": {
        "@type": "ContactPoint",
        "contactType": "customer support",
        "email": "support@equirize.com",
        "url": "https://www.equirize.com/contact"
      }
    },
    {
      "@type": "WebSite",
      "@id": "https://www.equirize.com/#website",
      "url": "https://www.equirize.com/",
      "name": "Equirize",
      "description": "Best Platform for Bonds & Fixed Deposits in India",
      "publisher": {
        "@id": "https://www.equirize.com/#organization"
      },
      "potentialAction": {
        "@type": "SearchAction",
        "target": {
          "@type": "EntryPoint",
          "urlTemplate": "https://www.equirize.com/search?q={search_term_string}"
        },
        "query-input": "required name=search_term_string"
      }
    },
    {
      "@type": "MobileApplication",
      "@id": "https://www.equirize.com/#ios-app",
      "name": APP_NAME,
      "operatingSystem": "iOS",
      "applicationCategory": "FinanceApplication",
      "url": APP_STORE_URL,
      "installUrl": APP_STORE_URL,
      "publisher": {
        "@id": "https://www.equirize.com/#organization"
      },
      "offers": {
        "@type": "Offer",
        "price": "0",
        "priceCurrency": "INR"
      }
    },
    {
      "@type": "MobileApplication",
      "@id": "https://www.equirize.com/#android-app",
      "name": APP_NAME,
      "operatingSystem": "Android",
      "applicationCategory": "FinanceApplication",
      "url": PLAY_STORE_URL,
      "installUrl": PLAY_STORE_URL,
      "identifier": ANDROID_PACKAGE_NAME,
      "publisher": {
        "@id": "https://www.equirize.com/#organization"
      },
      "offers": {
        "@type": "Offer",
        "price": "0",
        "priceCurrency": "INR"
      }
    }
  ]

});

const stepsData = [
  {
    title: "Step 1: Explore & Choose",
    description:
      "Browse listed bonds and bank or corporate FDs, compare returns, ratings & tenures, and select what fits your goals.",
    icon: `
      <svg xmlns="http://www.w3.org/2000/svg" height="24" width="24" fill="#0027AF">
        <path d="M0 0h24v24H0z" fill="none"/>
        <path d="M12 10.9c-.61 0-1.1.49-1.1 1.1s.49
        1.1 1.1 1.1c.61 0 1.1-.49 1.1-1.1s-.49-1.1-1.1-1.1zM12
        2C6.48 2 2 6.48 2 12s4.48 10
        10 10 10-4.48 10-10S17.52 2 12
        2zm2.19 12.19L6 18l3.81-8.19L18
        6l-3.81 8.19z"/>
      </svg>
    `
  },
  {
    title: "Step 2: Quick Digital Setup",
    description:
      "Complete KYC, link your bank, and invest securely in minutes.",
    icon: `
      <svg xmlns="http://www.w3.org/2000/svg" height="24" width="24" fill="#0027AF">
        <path d="M0 0h24v24H0z" fill="none"/>
        <path d="M20 4H4c-1.11 0-1.99.89-1.99
        2L2 18c0 1.11.89 2 2 2h16c1.11 0
        2-.89 2-2V6c0-1.11-.89-2-2-2zm0
        14H4v-6h16v6zm0-10H4V6h16v2z"/>
      </svg>
    `
  },
  {
    title: "Step 3: Track & Manage Seamlessly",
    description:
      "Monitor your investments, payouts, and maturity dates. Enable auto-renewal for continued growth.",
    icon: `
      <svg xmlns="http://www.w3.org/2000/svg" height="24" width="24" fill="#0027AF">
        <path fill="none" d="M0 0h24v24H0z"/>
        <path d="M19,3H5C3.9,3,3,3.9,3,5v14c0,
        1.1,0.9,2,2,2h14c1.1,0,2-0.9,2-2V5C21,
        3.9,20.1,3,19,3z M10.67,8.17l2,2l3.67-3.67
        l1.41,1.41L12.67,13l-2-2l-3,3l-1.41-1.41L10.67,
        8.17z"/>
      </svg>
    `
  }
];

const homeTitle = "Home | Best Platform for Bonds & Fixed Deposits in India | Equirize"
const homeDescription = "Equirize is India's regulated debt investment platform offering bonds and fixed deposits. Compare FD rates, invest in corporate bonds, and earn stable returns."
const homeUrl = 'https://www.equirize.com/'

useHead({
  title: homeTitle,
  meta: [
    { name: 'description', content: homeDescription },
    { name: 'keywords', content: 'EquiRize, Wealth Management, Digital Investing, Fixed Income, High Yield Investments, RBI Regulated FDs, SEBI Certified, BSE, NSE, SDG Investing, Secure Investments, Fintech Innovation, Purpose Driven Finance' },
    { property: 'og:type', content: 'website' },
    { property: 'og:title', content: homeTitle },
    { property: 'og:description', content: homeDescription },
    { property: 'og:url', content: homeUrl },
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:title', content: homeTitle },
    { name: 'twitter:description', content: homeDescription },
  ],
  link: [
    { rel: 'canonical', href: homeUrl }
  ],
  script: [
    {
      type: 'application/ld+json',
      innerHTML: schemaJson
    } as any
  ]
});
</script>
